import React, { useState } from "react";
import { Form, Input, Button } from "antd";
import { UserOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { requestPasswordReset } from "Common/Auth/authService";
import "./auth.css";

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await requestPasswordReset(values.email);
      setSent(true);
    } catch (error) {
      const data = error.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.message ||
        data?.title ||
        "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ciq-auth">
      <aside className="ciq-auth__brand">
        <div className="ciq-auth__brand-bg" />
        <div className="ciq-auth__brand-inner">
          <a className="ciq-brand" href="https://coreiqpro.com">
            <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="CoreIQPro" />
            <span className="ciq-brand__txt">
              Core<span className="o">IQ</span>Pro
            </span>
          </a>
          <h1 className="ciq-auth__headline">Reset your password securely.</h1>
          <p className="ciq-auth__sub">
            We will email you a secure link so you can set a new password and get back to learning.
          </p>
          <ul className="ciq-auth__points">
            <li>
              <i className="fas fa-check" /> Link is valid for 30 minutes
            </li>
            <li>
              <i className="fas fa-check" /> Works for teams and individuals
            </li>
            <li>
              <i className="fas fa-check" /> Your account stays protected
            </li>
          </ul>
        </div>
      </aside>

      <main className="ciq-auth__panel">
        <motion.div
          className="ciq-auth__card"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="ciq-auth__card-head">
            <div>
              <h2>Forgot password?</h2>
              <p className="ciq-auth__muted">Enter your email and we will send you a reset link.</p>
            </div>
          </div>

          {sent ? (
            <>
              <div className="ciq-auth__msg success">
                <div className="ciq-auth__msg-title">
                  <CheckCircleFilled /> Check your inbox
                </div>
                If an account exists for that email, a password reset link is on its way. The link is
                valid for 30 minutes.
              </div>
              <div className="ciq-auth__foot">
                <Button type="link" onClick={() => navigate("/login")}>
                  Back to Sign In
                </Button>
              </div>
            </>
          ) : (
            <>
              <Form name="forgot-form" layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please enter your email!" },
                    { type: "email", message: "Enter a valid email!" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="you@company.com"
                    autoComplete="email"
                    size="large"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <motion.div
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  >
                    <Button type="primary" htmlType="submit" block loading={loading} disabled={loading}>
                      {loading ? "Sending..." : "Send reset link"}
                    </Button>
                  </motion.div>
                </Form.Item>
              </Form>

              <div className="ciq-auth__foot">
                <Button type="link" onClick={() => navigate("/login")}>
                  Back to Sign In
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ForgotPassword;
